import { useState, useRef } from "react";
import { fmtDate } from "./shared";

export default function ScheduleMilestonesPanel({
  schedule,
  savingId,
  newMilestone,
  adding,
  onSave,
  onDelete,
  onNewMilestoneChange,
  onAdd,
  extracting,
  scheduleJobFailed,
  proposedMilestones,
  onExtract,
  onProposedChange,
  onDiscardProposed,
  onAcceptProposed,
  accepting,
  scheduleSummary,
}) {
  const [showUpload, setShowUpload]    = useState(false);
  const [fileDragging, setFileDragging] = useState(false);
  const [heroAcknowledged, setHeroAck] = useState(false);
  const [localOrder, setLocalOrder]    = useState(null);
  const [rowDragOver, setRowDragOver]  = useState(null);
  const dragIdx = useRef(null);
  const inputRef = useRef();

  const hasCustom  = schedule.some(m => m.IsCustom);
  const hasAnyDate = schedule.some(m => m.MilestoneDate);
  const showHero   = !heroAcknowledged && !hasCustom && !hasAnyDate && !proposedMilestones && !extracting;

  // Local order for drag-to-reorder; sync when schedule prop changes (e.g. after save)
  const displaySchedule = localOrder || schedule;

  const inputStyle = {
    width: "100%", padding: "4px 8px", fontSize: 13,
    border: "1px solid var(--cs-border)", borderRadius: 5,
    background: "var(--surface)", color: "var(--text)",
    fontFamily: "inherit",
  };

  function handleFile(file) {
    setShowUpload(false);
    setHeroAck(true);
    onExtract(file);
  }

  function handleFileDrop(e) {
    e.preventDefault();
    setFileDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function toggleProposed(id) {
    onProposedChange(prev => prev.map(m => m._id === id ? { ...m, _accepted: !m._accepted } : m));
  }

  function toggleAll(val) {
    onProposedChange(prev => prev.map(m => ({ ...m, _accepted: val })));
  }

  const selectedProposed = (proposedMilestones || []).filter(m => m._accepted);

  // ── Row drag-to-reorder ───────────────────────────────────────────────────────
  function handleRowDragStart(i) {
    dragIdx.current = i;
  }

  function handleRowDragOver(e, i) {
    e.preventDefault();
    setRowDragOver(i);
  }

  function handleRowDrop(e, toIdx) {
    e.preventDefault();
    setRowDragOver(null);
    const fromIdx = dragIdx.current;
    if (fromIdx === null || fromIdx === toIdx) return;
    const reordered = [...displaySchedule];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setLocalOrder(reordered);
    // Persist new DisplayOrder for every row that moved
    reordered.forEach((m, idx) => {
      const newOrder = (idx + 1) * 10;
      if (m.DisplayOrder !== newOrder) {
        onSave({ ...m, DisplayOrder: newOrder });
      }
    });
    dragIdx.current = null;
  }

  function handleRowDragEnd() {
    setRowDragOver(null);
    dragIdx.current = null;
  }

  // ── Two-path hero ─────────────────────────────────────────────────────────────
  if (showHero) {
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)", marginBottom: 16 }}>
          Key Project Dates
        </div>
        <div style={{
          padding: "28px 24px", border: "1px solid var(--cs-border)",
          borderRadius: 10, background: "#f8f9fb", textAlign: "center", marginBottom: 16,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>
            How would you like to build out this schedule?
          </div>
          {scheduleJobFailed && (
            <div style={{
              display: "inline-block", marginBottom: 14, padding: "6px 12px",
              background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6,
              fontSize: 12, color: "#dc2626",
            }}>
              Last extraction attempt failed — try uploading again or use defaults.
            </div>
          )}
          <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 24 }}>
            Upload a GC schedule PDF to auto-extract milestones, or define the key dates manually.
          </div>
          <div style={{ display: "flex", gap: 0, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
            {/* Upload path */}
            <div
              onClick={() => { setHeroAck(true); setShowUpload(true); }}
              style={{
                width: 220, padding: "20px 16px", border: "2px solid var(--cs-blue)",
                borderRadius: 10, background: "#fff", cursor: "pointer", textAlign: "left",
                transition: "box-shadow 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
            >
              <div style={{ fontSize: 22, marginBottom: 8 }}>⬆</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cs-blue)", marginBottom: 4 }}>
                Upload Schedule File
              </div>
              <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.5 }}>
                Extract milestones automatically from a Primavera P6 or MS Project PDF. CleanSpace-relevant dates are flagged.
              </div>
            </div>

            {/* "or" divider */}
            <div style={{ padding: "0 20px", fontSize: 12, fontWeight: 600, color: "var(--text3)" }}>or</div>

            {/* Define dates path */}
            <div
              onClick={() => setHeroAck(true)}
              style={{
                width: 220, padding: "20px 16px", border: "1px solid var(--cs-border)",
                borderRadius: 10, background: "#fff", cursor: "pointer", textAlign: "left",
                transition: "box-shadow 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
            >
              <div style={{ fontSize: 22, marginBottom: 8 }}>📋</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>
                Define Milestone Dates
              </div>
              <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.5 }}>
                Fill in CleanSpace's standard milestone set manually — kick-off, fabrication, installation, substantial completion.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 24 }}>

      {/* ── AI Schedule Summary ── */}
      {scheduleSummary && (
        <div style={{
          marginBottom: 16, padding: "14px 16px",
          background: "var(--surface)", border: "1px solid var(--cs-border)",
          borderRadius: 8, borderLeft: "3px solid var(--cs-blue)",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--cs-blue)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            AI Schedule Summary
          </div>
          <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {scheduleSummary}
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)" }}>Key Project Dates</div>
        {!proposedMilestones && (
          <button
            onClick={() => setShowUpload(v => !v)}
            disabled={extracting}
            style={{
              padding: "5px 14px", fontSize: 12, fontWeight: 600,
              background: extracting ? "#e5e7eb" : "var(--cs-blue)",
              color: extracting ? "#9ca3af" : "#fff",
              border: "none", borderRadius: 6,
              cursor: extracting ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {extracting ? "Extracting…" : "⬆ Extract from Schedule"}
          </button>
        )}
      </div>

      {/* ── Drop zone ── */}
      {showUpload && !extracting && (
        <div
          onDragOver={e => { e.preventDefault(); setFileDragging(true); }}
          onDragLeave={() => setFileDragging(false)}
          onDrop={handleFileDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            marginBottom: 16, padding: "24px 16px", textAlign: "center",
            border: `2px dashed ${fileDragging ? "var(--cs-blue)" : "var(--cs-border)"}`,
            borderRadius: 8, background: fileDragging ? "#eff6ff" : "#f8f9fb",
            cursor: "pointer", transition: "all 0.15s",
          }}
        >
          <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 4 }}>
            Drop a schedule PDF here, or click to browse
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)" }}>
            Primavera P6 exports, MS Project PDFs, or any GC schedule
          </div>
          <input ref={inputRef} type="file" accept=".pdf,image/*" style={{ display: "none" }}
            onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }} />
        </div>
      )}

      {/* ── Extracting spinner (background job) ── */}
      {extracting && (
        <div style={{
          marginBottom: 16, padding: "16px 20px",
          border: "1px solid #bae6fd", borderRadius: 8, background: "#f0f9ff",
          display: "flex", alignItems: "center", gap: 14,
        }}>
          <div style={{ fontSize: 20 }}>⏳</div>
          <div>
            <div style={{ fontSize: 13, color: "#0369a1", fontWeight: 600 }}>
              Schedule analysis running in the background
            </div>
            <div style={{ fontSize: 11, color: "#0369a1", marginTop: 3 }}>
              You can navigate away — milestones will be ready for review when you return to this section.
            </div>
          </div>
        </div>
      )}

      {/* ── Failed state ── */}
      {scheduleJobFailed && !extracting && !proposedMilestones && (
        <div style={{
          marginBottom: 16, padding: "12px 16px",
          border: "1px solid #fca5a5", borderRadius: 8, background: "#fef2f2",
          fontSize: 13, color: "#dc2626",
        }}>
          Schedule analysis failed. Check the console for details, then try uploading again.
        </div>
      )}

      {/* ── Proposed milestones review ── */}
      {proposedMilestones && !extracting && (
        <div style={{ marginBottom: 20, border: "1px solid #bae6fd", borderRadius: 8, overflow: "hidden" }}>
          <div style={{
            padding: "10px 14px", background: "#f0f9ff", borderBottom: "1px solid #bae6fd",
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0369a1" }}>
                {proposedMilestones.length} milestones extracted
              </div>
              <div style={{ fontSize: 11, color: "#0369a1", marginTop: 1 }}>
                Review and check the ones to add. CleanSpace-relevant rows are pre-selected.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
              <button onClick={() => toggleAll(true)}
                style={{ fontSize: 11, color: "#0369a1", background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>
                Select all
              </button>
              <span style={{ color: "#bae6fd" }}>|</span>
              <button onClick={() => toggleAll(false)}
                style={{ fontSize: 11, color: "#0369a1", background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>
                Deselect all
              </button>
              <button
                onClick={() => onAcceptProposed(selectedProposed)}
                disabled={!selectedProposed.length || accepting}
                style={{
                  padding: "5px 14px", fontSize: 12, fontWeight: 700,
                  background: selectedProposed.length && !accepting ? "var(--cs-blue)" : "#e5e7eb",
                  color: selectedProposed.length && !accepting ? "#fff" : "#9ca3af",
                  border: "none", borderRadius: 6,
                  cursor: selectedProposed.length && !accepting ? "pointer" : "not-allowed",
                }}
              >
                {accepting ? "Adding…" : `Add ${selectedProposed.length} selected`}
              </button>
              <button onClick={onDiscardProposed || (() => onProposedChange(null))} disabled={accepting}
                style={{ padding: "5px 10px", fontSize: 12, background: "none", border: "1px solid #bae6fd", borderRadius: 6, cursor: "pointer", color: "#0369a1" }}>
                Discard
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "32px 1fr 120px 120px 1fr", background: "#f8f9fb", borderBottom: "1px solid var(--cs-border)" }}>
            {["", "Milestone", "Start", "Finish", "Notes"].map(h => (
              <div key={h} style={{ padding: "6px 10px", fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
            ))}
          </div>

          {proposedMilestones.map((m, i) => (
            <div key={m._id} style={{
              display: "grid", gridTemplateColumns: "32px 1fr 120px 120px 1fr",
              borderBottom: i < proposedMilestones.length - 1 ? "1px solid var(--cs-border)" : "none",
              background: m.cs_relevant ? (m._accepted ? "#f0fdf4" : "transparent") : (m._accepted ? "#fafbfc" : "transparent"),
              alignItems: "start",
            }}>
              <div style={{ padding: "8px 8px 0", display: "flex", justifyContent: "center" }}>
                <input type="checkbox" checked={!!m._accepted} onChange={() => toggleProposed(m._id)} style={{ cursor: "pointer", marginTop: 2 }} />
              </div>
              <div style={{ padding: "8px 10px" }}>
                <div style={{ fontSize: 13, color: "var(--text)", fontWeight: m.cs_relevant ? 600 : 400 }}>{m.label}</div>
                {m.cs_relevant && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#065f46", background: "#d1fae5", padding: "1px 6px", borderRadius: 100, marginTop: 3, display: "inline-block" }}>
                    CleanSpace scope
                  </span>
                )}
              </div>
              <div style={{ padding: "8px 10px", fontSize: 12, color: m.start_date ? "var(--text)" : "var(--text3)" }}>
                {fmtDate(m.start_date)}
              </div>
              <div style={{ padding: "8px 10px", fontSize: 12, color: m.end_date ? "var(--text)" : "var(--text3)" }}>
                {fmtDate(m.end_date)}
              </div>
              <div style={{ padding: "8px 10px", fontSize: 11, color: "var(--text3)", lineHeight: 1.4 }}>
                {m.notes || ""}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Saved milestone table ── */}
      <div style={{ border: "1px solid var(--cs-border)", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "28px 1fr 160px 1fr 36px", background: "#f8f9fb", borderBottom: "1px solid var(--cs-border)" }}>
          {["", "Milestone", "Date", "Notes", ""].map((h, i) => (
            <div key={i} style={{ padding: "8px 14px", fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
          ))}
        </div>

        {displaySchedule.map((m, i) => (
          <div
            key={m.TurnoverScheduleID}
            draggable
            onDragStart={() => handleRowDragStart(i)}
            onDragOver={e => handleRowDragOver(e, i)}
            onDrop={e => handleRowDrop(e, i)}
            onDragEnd={handleRowDragEnd}
            style={{
              display: "grid", gridTemplateColumns: "28px 1fr 160px 1fr 36px",
              borderBottom: i < displaySchedule.length - 1 ? "1px solid var(--cs-border)" : "none",
              borderLeft: m.IsCSRelevant !== false && m.IsCSRelevant !== 0 ? "3px solid var(--cs-blue)" : "3px solid #bfdbfe",
              alignItems: "center",
              background: rowDragOver === i
                ? "#dbeafe"
                : (m.IsCSRelevant !== false && m.IsCSRelevant !== 0 ? "#eff6ff" : "#f0f4f8"),
              opacity: savingId === m.TurnoverScheduleID ? 0.5 : 1,
              transition: "background 0.1s",
            }}
          >
            {/* Drag handle */}
            <div
              style={{
                padding: "0 6px", cursor: "grab", color: "#cbd5e1", fontSize: 14,
                display: "flex", alignItems: "center", justifyContent: "center", height: "100%",
                userSelect: "none",
              }}
              title="Drag to reorder"
            >
              ⠿
            </div>
            <div style={{ padding: "8px 14px" }}>
              {m.IsCustom ? (
                <input
                  defaultValue={m.MilestoneLabel}
                  onBlur={e => { if (e.target.value !== m.MilestoneLabel) onSave({ ...m, MilestoneLabel: e.target.value }); }}
                  style={{ width: "100%", border: "none", background: "transparent", fontSize: 13, color: "var(--text)", fontFamily: "inherit", outline: "none", fontStyle: "italic" }}
                />
              ) : (
                <span style={{ fontSize: 13, color: "var(--text)", fontWeight: m.MilestoneDate ? 600 : 400 }}>
                  {m.MilestoneLabel}
                </span>
              )}
            </div>
            <div style={{ padding: "6px 14px" }}>
              <input
                type="date"
                defaultValue={m.MilestoneDate ? m.MilestoneDate.split("T")[0] : ""}
                onBlur={e => onSave({ ...m, MilestoneDate: e.target.value || null })}
                style={{ ...inputStyle, fontSize: 12 }}
              />
            </div>
            <div style={{ padding: "6px 14px" }}>
              <input
                defaultValue={m.Notes || ""}
                placeholder="Add note…"
                onBlur={e => { if (e.target.value !== (m.Notes || "")) onSave({ ...m, Notes: e.target.value || null }); }}
                style={{ ...inputStyle, fontSize: 12, color: m.Notes ? "var(--text)" : "var(--text3)" }}
              />
            </div>
            <div style={{ padding: "6px 8px", textAlign: "center" }}>
              <button onClick={() => onDelete(m)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#d1d5db", padding: "2px 4px" }}
                onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}>
                🗑
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Add custom milestone ── */}
      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
        <input
          value={newMilestone.label}
          onChange={e => onNewMilestoneChange({ ...newMilestone, label: e.target.value })}
          placeholder="Add custom milestone…"
          style={{ flex: 1, padding: "6px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
        />
        <input
          type="date"
          value={newMilestone.date}
          onChange={e => onNewMilestoneChange({ ...newMilestone, date: e.target.value })}
          style={{ padding: "6px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
        />
        <button
          onClick={onAdd}
          disabled={adding || !newMilestone.label.trim()}
          style={{
            padding: "6px 16px", fontSize: 13, fontWeight: 700,
            background: "var(--cs-blue)", color: "#fff",
            border: "none", borderRadius: 6, cursor: "pointer",
            opacity: (adding || !newMilestone.label.trim()) ? 0.5 : 1,
            whiteSpace: "nowrap",
          }}
        >
          {adding ? "Adding…" : "+ Add"}
        </button>
      </div>
    </div>
  );
}
